// covered-by: packages/schemas/src/__tests__/knowledge-rule-schema.spec.ts
import { z } from "zod";

import { citationCellAnchorSchema } from "./ticket";

/**
 * Shared building blocks for the refreshed Knowledge Database:
 * sources (uploaded guideline files), rules (synthesized from extractions),
 * and the append-only activity log.
 *
 * `interaction_memory` and `reviewed_asset` are RESERVED source types for the
 * future low-trust interaction pipeline — declared now so those sources land
 * without schema churn.
 */
export enum KnowledgeSourceType {
  GUIDELINE_DOCUMENT = "guideline_document",
  INTERACTION_MEMORY = "interaction_memory",
  REVIEWED_ASSET = "reviewed_asset",
}

export enum KnowledgeTrustLevel {
  HIGH = "high",
  LOW = "low",
}

/** Trust is derived from the source type on the server — never client input. */
export const trustLevelForSourceType = (
  sourceType: KnowledgeSourceType,
): KnowledgeTrustLevel =>
  sourceType === KnowledgeSourceType.GUIDELINE_DOCUMENT
    ? KnowledgeTrustLevel.HIGH
    : KnowledgeTrustLevel.LOW;

export enum KnowledgeScopeSource {
  INFERRED = "inferred",
  USER = "user",
  NONE = "none",
}

export const knowledgeScopeSchema = z.object({
  brand: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  team: z.string().nullable().optional(),
});
export type KnowledgeScope = z.infer<typeof knowledgeScopeSchema>;

/**
 * Cited location inside a knowledge source. Mirrors `fileCitationSchema`
 * minus `url` — files live behind the source's s3Key and URLs are presigned
 * at read time, so stored citations never go stale. Coordinates are
 * denormalized at extraction time (never resolved lazily from the 90-day-TTL
 * aicache collections). All fields optional: a quote-only citation (no
 * anchorable location) is valid.
 */
export const knowledgeCitationLocationSchema = z.object({
  pageNumber: z.coerce.number().int().min(1).optional(),
  rel_x: z.coerce.number().min(0).max(1).optional(),
  rel_y: z.coerce.number().min(0).max(1).optional(),
  bbox: z
    .object({
      x: z.coerce.number(),
      y: z.coerce.number(),
      w: z.coerce.number(),
      h: z.coerce.number(),
    })
    .optional(),
  cellAnchor: citationCellAnchorSchema.optional(),
});
export type KnowledgeCitationLocation = z.infer<
  typeof knowledgeCitationLocationSchema
>;

/**
 * A rule's link to its exact source evidence. `quote` is MANDATORY — a rule
 * citation without verbatim source text is not a citation. `extractionId`
 * back-references the raw `knowledge_extractions` row (rembrandt-owned) that
 * produced it. Citations are ordered: index 0 is primary, sorted
 * high-trust-first at write time.
 */
export const knowledgeRuleCitationSchema = z.object({
  sourceId: z.string().min(1),
  extractionId: z.string().optional(),
  trustLevel: z.nativeEnum(KnowledgeTrustLevel),
  quote: z.string().min(1),
  location: knowledgeCitationLocationSchema,
});
export type KnowledgeRuleCitation = z.infer<typeof knowledgeRuleCitationSchema>;
